import { Action, ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import popInSessionsSlice, {
	PopInSession,
	popInSessionEventsAdapter,
	PopInSessionPayload,
	PopInSessionUpdate,
	popInSessionUserSessionsAdapter,
} from "./slices/popInSessions";
import usersApi from "./slices/users";
import { RootState } from "./index";
import assessmentsApi from "./slices/assessments";
import { uuidToIntId } from "../../../../helpers/uuid";

type AppThunkAction = ThunkAction<void, RootState, unknown, Action>;
type AppDispatch = ThunkDispatch<RootState, unknown, Action>;

const {
	popInSessionStarted: popInSessionStartedInternal,
	popInSessionUpdated: popInSessionUpdatedInternal,
	fullStateUpdated: fullStateUpdatedInternal,
} = popInSessionsSlice.actions;

export const {
	popInSessionEnded,
	popInSessionUserJoined,
	popInSessionUserLeft,
	popInSessionEventCreated,
	popInSessionEventUpdated,
	resetState,
	errorLoading,
} = popInSessionsSlice.actions;

function findSessionStudentIds(session: Pick<PopInSessionPayload, "userSessions">) {
	const studentSession = Object.values(session.userSessions).find(({ role }) => role === "student");
	return { id: studentSession?.id, userId: studentSession?.userId };
}

async function convertAndFetchSessionInfo(
	session: PopInSessionPayload,
	rootState: RootState,
	dispatch: AppDispatch,
	currentUserId?: string,
): Promise<PopInSession> {
	const { assessmentId, lastViewedTimestamp, userSessions, events } = session;
	const { id: studentUserSessionId, userId: studentUuid } = findSessionStudentIds(session);
	if (studentUuid && !usersApi.endpoints.getUser.select(studentUuid)(rootState)) {
		await dispatch(usersApi.endpoints.getUser.initiate(studentUuid));
	}
	if (assessmentId && !assessmentsApi.endpoints.getAssessment.select(uuidToIntId(assessmentId))) {
		await dispatch(assessmentsApi.endpoints.getAssessment.initiate(uuidToIntId(assessmentId)));
	}
	return {
		...session,
		studentId: studentUuid,
		events: popInSessionEventsAdapter.setAll(
			popInSessionEventsAdapter.getInitialState(),
			Object.fromEntries(Object.entries(events).filter(([, { payload }]) => payload?.flagLevel)),
		),
		userSessions: popInSessionUserSessionsAdapter.setAll(
			popInSessionUserSessionsAdapter.getInitialState(),
			Object.values(userSessions).filter(({ role }) => role === "proctor"),
		),
		studentUserSessionId,
		lastViewedTimestamp: currentUserId ? lastViewedTimestamp[currentUserId] : undefined,
	};
}

export function popInSessionStarted(session: PopInSessionPayload): AppThunkAction {
	return async (dispatch, getState) => {
		const rootState = getState();
		dispatch(
			popInSessionStartedInternal(
				await convertAndFetchSessionInfo(
					session,
					rootState,
					dispatch,
					usersApi.endpoints.getCurrentUserId.select()(rootState).data,
				),
			),
		);
	};
}

export function popInSessionUpdated(session: PopInSessionUpdate): AppThunkAction {
	return async (dispatch, getState) => {
		const currentUserId = usersApi.endpoints.getCurrentUserId.select()(getState()).data;
		dispatch(
			popInSessionUpdatedInternal({
				...session,
				lastViewedTimestamp: currentUserId ? session.lastViewedTimestamp[currentUserId] : undefined,
			}),
		);
	};
}

export function fullStateUpdated(sessions: Record<string, PopInSessionPayload>): AppThunkAction {
	return async (dispatch, getState) => {
		const currState = getState();
		const userResult = await dispatch(usersApi.endpoints.getCurrentUserId.initiate());
		const convertedSessions = await Promise.all(
			Object.values(sessions)
				.filter(({ userSessions }) =>
					Object.values(userSessions).some(
						({ role, lastPingTime }) =>
							role === "student" && lastPingTime && lastPingTime > Date.now() - 10 * 60 * 1000,
					),
				)
				.map((session) => convertAndFetchSessionInfo(session, currState, dispatch, userResult.data)),
		);
		dispatch(fullStateUpdatedInternal(convertedSessions));
	};
}
